import { useNavigate } from 'react-router-dom';
import {
  ArrowDown,
  Award,
  Bookmark,
  Building2,
  CheckCircle2,
  Filter,
  MapPin,
  MessageSquare,
  Search,
  ShieldCheck,
  Star,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useVendorFavorite } from '../../hooks/useVendorFavorite';
import {
  getOwnerProjects,
  saveOwnerProjects,
} from '../../data/ownerPrototype';
import { useCreateChannel } from '../../../hooks/useChat';

const VendorTersimpan = () => {
  const navigate = useNavigate();
  const { savedVendors: savedProviders, isLoading, removeVendor } = useVendorFavorite();
  const createChannelMutation = useCreateChannel();

  const handleChatVendor = (vendorId: string | undefined) => {
    if (!vendorId) return;
    
    createChannelMutation.mutate(
      { to_user: vendorId },
      {
        onSuccess: (data) => {
          navigate(`/owner/pesan?channel_id=${data.id}`);
        },
        onError: (error: any) => {
          console.error('Chat vendor error:', error);
          const errMsg = error?.response?.data?.message || error?.message || 'Terjadi kesalahan sistem.';
          toast.error(`Gagal membuka obrolan: ${errMsg}`);
        }
      }
    );
  };

  const savedVendors = useMemo(() => {
    return savedProviders.map((fav: any) => {
      const p = fav.provider || {};
      const s = fav.serviceman;
      const u = s?.user;
      
      let name = p.company_name || p.contact_person_name || 'Vendor Tanpa Nama';
      let title = name;
      
      let rating = p.avg_rating || 0;
      let reviews = p.rating_count || 0;
      let projects = p.total_service_served || 0;
      let teamSize = 'Tim';

      if (s && u) {
          title = `${u.first_name} ${u.last_name}`;
          name = `${title} (Mandor: ${p.company_name || 'Independen'})`;

          if (s.parsed_metadata) {
              reviews = s.parsed_metadata.review_count || reviews;
              // Extract numbers from "120 Proyek"
              const parsedProj = parseInt(String(s.parsed_metadata.proyek_selesai).replace(/\D/g, ''));
              if (!isNaN(parsedProj)) {
                  projects = parsedProj;
              }
              teamSize = s.parsed_metadata.tim || teamSize;
          }
      }
      
      return {
        id: fav.id,
        providerId: fav.provider_id,
        servicemanId: fav.serviceman_id,
        userId: p.user_id || s?.user_id,
        name: name,
        title: title,
        category: s ? 'Tukang/Arsitek' : 'Vendor Jasa',
        location: p.company_address || 'Alamat tidak tersedia',
        tags: ['Kontraktor'],
        badge: p.is_approved === 1 ? 'Verified' : 'Mitra',
        rating: rating,
        description: p.about || 'Tidak ada deskripsi yang tersedia.',
        reviews: reviews,
        projects: projects,
        teamSize: teamSize,
      };
    });
  }, [savedProviders]);

  const [projects, setProjects] = useState(() => getOwnerProjects());
  const [selectedProjectId, setSelectedProjectId] = useState(() => getOwnerProjects()[0]?.id ?? '');
  const [invitedIds, setInvitedIds] = useState<string[]>([]);
  const [query, setQuery] = useState('');

  const filteredVendors = useMemo(
    () =>
      savedVendors.filter((vendor) =>
        `${vendor.name} ${vendor.category} ${vendor.location} ${vendor.tags.join(' ')}`
          .toLowerCase()
          .includes(query.toLowerCase()),
      ),
    [savedVendors, query],
  );

  const handleInvite = (id: string, name: string) => {
    if (!selectedProjectId) {
      toast.error('Pilih proyek tujuan RFQ terlebih dahulu.');
      return;
    }

    const targetProject = projects.find((project) => project.id === selectedProjectId);
    const nextProjects = projects.map((project) =>
      project.id === selectedProjectId
        ? {
            ...project,
            invitedVendorIds: Array.from(new Set([...(project.invitedVendorIds ?? []), id])),
            messages: [
              ...project.messages,
              {
                id: `vendor-invite-${Date.now()}`,
                sender: 'system' as const,
                body: `${name} diundang RFQ untuk proyek ${project.title}.`,
                createdAt: new Date().toISOString(),
              },
            ],
          }
        : project,
    );

    setProjects(nextProjects);
    saveOwnerProjects(nextProjects);
    setInvitedIds((previous) => (previous.includes(id) ? previous : [...previous, id]));
    toast.success(`Undangan RFQ dikirim ke ${name}${targetProject ? ` untuk ${targetProject.title}` : ''}.`);
  };

  const handleRemoveVendor = (vendor: any) => {
    removeVendor({ providerId: vendor.providerId, servicemanId: vendor.servicemanId });
  };

  const verifiedCount = savedVendors.filter((vendor) => vendor.badge.toLowerCase().includes('verified')).length;
  const averageRating = savedVendors.length
    ? (savedVendors.reduce((total, vendor) => total + vendor.rating, 0) / savedVendors.length).toFixed(1)
    : '0.0';
  const categoryCount = new Set(savedVendors.flatMap((vendor) => vendor.tags)).size;

  return (
    <div className="space-y-6">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Vendor Tersimpan</h1>
          <p className="mt-2 text-sm text-slate-500">
            Kelola shortlist vendor B2B untuk kebutuhan tender, RFQ, renovasi, dan maintenance berikutnya.
          </p>
        </div>
        
      </section>

      <section className="grid gap-4 md:grid-cols-4">
        {[
          [String(savedVendors.length), 'Vendor tersimpan', Bookmark],
          [String(verifiedCount), 'Verified vendor', ShieldCheck],
          [averageRating, 'Rata-rata rating', Star],
          [String(categoryCount), 'Kategori jasa', Building2],
        ].map(([value, label, Icon]) => (
          <div key={label as string} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-extrabold text-slate-900">{value as string}</p>
                <p className="mt-1 text-sm font-semibold text-slate-500">{label as string}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-700">
                <Icon size={24} />
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
        <aside className="order-2 h-fit w-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:sticky lg:top-24 lg:w-[280px]">
          <h2 className="font-extrabold text-slate-900">Undang ke Proyek</h2>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            Pilih proyek tujuan, lalu klik Undang RFQ pada vendor.
          </p>
          <label className="mt-4 block space-y-2">
            <span className="text-sm font-bold text-slate-700">Proyek Tujuan</span>
            <select
              value={selectedProjectId}
              onChange={(event) => setSelectedProjectId(event.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
            >
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.title}
                </option>
              ))}
            </select>
          </label>
          <div className="mt-5 rounded-2xl bg-blue-50 p-4 text-sm font-semibold leading-6 text-blue-800">
            Vendor yang diundang akan tercatat di proyek sebagai shortlist RFQ/Tender Terbatas.
          </div>

          <h2 className="mt-6 font-extrabold text-slate-900">Filter Vendor</h2>
          <div className="mt-5 space-y-4">
            {['Kategori Jasa', 'Lokasi', 'Rating Minimal', 'Status Verifikasi'].map((label) => (
              <label key={label} className="block space-y-2">
                <span className="text-sm font-bold text-slate-700">{label}</span>
                <select className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100">
                  <option>Semua {label}</option>
                  <option>Verified</option>
                  <option>Top Rated</option>
                </select>
              </label>
            ))}
            <button className="w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-extrabold text-white">Terapkan Filter</button>
          </div>
        </aside>

        <div className="order-1 min-w-0 space-y-4">
          <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 lg:grid-cols-[minmax(260px,1fr)_120px_120px]">
            <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-500">
              <Search size={17} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="w-full bg-transparent outline-none"
                placeholder="Cari vendor, kategori, lokasi..."
              />
            </label>
            <button className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 px-3 py-2.5 text-sm font-bold text-blue-700">
              <Filter size={16} />
              Filter
            </button>
            <button className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-bold text-slate-600">
              Rating
              <ArrowDown size={16} />
            </button>
          </div>

          <div className="grid gap-4 2xl:grid-cols-2">
            {isLoading && (
              <div className="col-span-2 py-10 text-center text-sm font-semibold text-slate-400">
                Memuat data vendor...
              </div>
            )}
            {!isLoading && filteredVendors.map((vendor) => (
              <article key={vendor.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-blue-100 hover:shadow-md">
                <div className="flex items-start gap-4">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-xl font-extrabold text-blue-700">
                    {vendor.title.slice(0, 2)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg font-extrabold text-slate-900">{vendor.name}</h2>
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-extrabold text-emerald-700">
                        <CheckCircle2 size={13} />
                        {vendor.badge}
                      </span>
                    </div>
                    <p className="mt-1 text-sm font-semibold text-blue-700">{vendor.category}</p>
                    <p className="mt-3 text-sm leading-6 text-slate-500">{vendor.description}</p>
                  </div>
                  <button onClick={() => handleRemoveVendor(vendor)} type="button" aria-label="Hapus vendor tersimpan">
                    <Bookmark size={20} className="fill-blue-600 text-blue-600" />
                  </button>
                </div>

                <div className="mt-5 grid gap-3 text-sm text-slate-600 sm:grid-cols-2">
                  <span className="inline-flex items-center gap-2">
                    <MapPin size={16} className="text-slate-400" />
                    {vendor.location}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <Star size={16} className="fill-amber-400 text-amber-400" />
                    {vendor.rating} ({vendor.reviews} ulasan)
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <Award size={16} className="text-slate-400" />
                    {vendor.projects} proyek selesai
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <Users size={16} className="text-slate-400" />
                    {vendor.teamSize}
                  </span>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  {vendor.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-5 grid gap-2 ">
                  {/* <button onClick={() => handleInvite(vendor.providerId, vendor.name)} className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-extrabold text-white">
                    {invitedIds.includes(vendor.providerId) ? 'RFQ Terkirim' : 'Undang RFQ'}
                  </button> */}
                  <button 
                    type="button"
                    onClick={() => handleChatVendor(vendor.userId || vendor.providerId)}
                    disabled={createChannelMutation.isPending}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-extrabold text-blue-700 disabled:opacity-50 cursor-pointer"
                  >
                    <MessageSquare size={16} />
                    {createChannelMutation.isPending ? 'Membuka...' : 'Chat Vendor'}
                  </button>
                </div>
              </article>
            ))}
            {!isLoading && filteredVendors.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
                <p className="text-sm font-semibold text-slate-400">Vendor tidak ditemukan atau Anda belum menyimpan vendor apa pun.</p>
                <a
                  href={`${import.meta.env.VITE_API_BASE_URL}/marketplace/servicemen?type=teknisi`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center justify-center rounded-xl bg-blue-600 px-6 py-2.5 text-sm font-extrabold text-white hover:bg-blue-700"
                >
                  Jelajahi Vendor Marketplace
                </a>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default VendorTersimpan;
